import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { DollarSign, Download, FileText, TrendingUp, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow } from
'@/shared/components/ui/table';
import { formatCurrency, formatDate } from '@/shared/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter } from
'@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import { toast } from 'sonner';

const API_URL = API_BASE;

export const Billing = () => {const { t } = useTranslation("ezparcel");
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    outstanding: 0,
    overdue: 0,
    codCollected: 0,
    pendingCount: 0,
    overdueCount: 0,
    transactions: 0
  });
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    amount: '',
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    notes: ''
  });

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('ezerpToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [invoicesRes, statsRes] = await Promise.all([
      fetch(`${API_URL}/api/ezparcel/billing/invoices`, { headers }),
      fetch(`${API_URL}/api/ezparcel/billing/stats`, { headers })]
      );

      if (invoicesRes.ok) setInvoices(await invoicesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingData();
  }, []);

  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('ezerpToken');
      const response = await fetch(`${API_URL}/api/ezparcel/billing/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Invoice generated successfully');
        setIsModalOpen(false);
        setFormData({
          customer_name: '',
          amount: '',
          due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          notes: ''
        });
        fetchBillingData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate invoice');
      }
    } catch (err) {
      console.error('Generate invoice error:', err);
      toast.error('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--accent))]" />
      </div>);

  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("billingInvoices", "Billing & Invoices")}</h1>
          <p className="text-muted-foreground mt-2">{t("manageInvoicesPayments", "Manage invoices, payments, and financial records")}

          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
          
          <FileText className="mr-2 h-4 w-4" />{t("generateInvoice", "Generate Invoice")}

        </Button>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t("generateNewInvoice", "Generate New Invoice")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGenerateInvoice} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="customer_name">{t("customer", "Customer")}</Label>
              <Input
                id="customer_name"
                required
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="Select or enter customer name" />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">{t("amount", "Amount ($)")}</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  required
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00" />
                
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date">{t("dueDate", "Due Date")}</Label>
                <Input
                  id="due_date"
                  type="date"
                  required
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
                
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t("notes", "Notes")}</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional invoice details..." />
              
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>{t("cancel", "Cancel")}

              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-blue-600 hover:bg-blue-700">
                {isSubmitting ?
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("generating", "Generating...")}

                </> :

                'Generate Invoice'
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid gap-6 md:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm font-medium text-muted-foreground">{t("totalRevenue", "Total Revenue")}</p>
            </div>
            <p className="text-3xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
            <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
              <TrendingUp className="h-4 w-4" />{t("184VsLast", "+18.4% vs last month")}

            </p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">{t("outstanding", "Outstanding")}</p>
            <p className="text-3xl font-bold">{formatCurrency(stats.outstanding)}</p>
            <p className="text-sm text-muted-foreground mt-2">{stats.pendingCount}{t("invoicesPending", "invoices pending")}</p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">{t("overdue", "Overdue")}</p>
            <p className="text-3xl font-bold text-red-600">{formatCurrency(stats.overdue)}</p>
            <p className="text-sm text-muted-foreground mt-2">{stats.overdueCount}{t("invoicesOverdue", "invoices overdue")}</p>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-muted-foreground mb-2">{t("codCollected", "COD Collected")}</p>
            <p className="text-3xl font-bold">{formatCurrency(stats.codCollected)}</p>
            <p className="text-sm text-muted-foreground mt-2">{stats.transactions}{t("transactions", "transactions")}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("recentInvoices", "Recent Invoices")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("invoiceId", "Invoice ID")}</TableHead>
                <TableHead>{t("customer", "Customer")}</TableHead>
                <TableHead>{t("amount", "Amount")}</TableHead>
                <TableHead>{t("issueDate", "Issue Date")}</TableHead>
                <TableHead>{t("dueDate", "Due Date")}</TableHead>
                <TableHead>{t("status", "Status")}</TableHead>
                <TableHead className="text-right">{t("actions", "Actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) =>
              <TableRow key={invoice.id}>
                  <TableCell className="font-mono font-medium">{invoice.id}</TableCell>
                  <TableCell>{invoice.customer}</TableCell>
                  <TableCell className="font-semibold">{formatCurrency(invoice.amount)}</TableCell>
                  <TableCell>{formatDate(invoice.date)}</TableCell>
                  <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                  <TableCell>
                    <Badge
                    className={`status-badge ${invoice.status === 'paid' ?
                    'status-completed' :
                    invoice.status === 'pending' ?
                    'status-pending' :
                    'priority-high'}`
                    }>
                    
                      {invoice.status.toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4 mr-2" />{t("download", "Download")}

                  </Button>
                  </TableCell>
                </TableRow>
              )}
              {invoices.length === 0 &&
              <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">{t("noInvoicesFound", "No invoices found")}

                </TableCell>
                </TableRow>
              }
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>);

};